import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Plus, Trash2, Layers, X, Briefcase } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../../components/RowActions";

const EMPTY = { school_id: "demo-school-1", name_ar: "", name: "", code: "", manager_user_id: null, positions: [] };

export default function DepartmentsPage() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [newPos, setNewPos] = useState({ key: "", label_ar: "", label_en: "" });

  const load = () => api.get("/departments").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setNewPos({ key: "", label_ar: "", label_en: "" }); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...EMPTY, ...it, positions: it.positions || [] });
    setOpen(true);
  };

  const addPosition = () => {
    if (!newPos.label_ar.trim()) { toast.error(t("position_label_ar")); return; }
    const key = (newPos.key || newPos.label_en || newPos.label_ar).trim().toLowerCase().replace(/\s+/g, "_");
    if (form.positions.find(p => p.key === key)) { toast.error(t("dept_already_exists")); return; }
    setForm({ ...form, positions: [...form.positions, { ...newPos, key }] });
    setNewPos({ key: "", label_ar: "", label_en: "" });
  };
  const removePosition = (key) => {
    setForm({ ...form, positions: form.positions.filter(p => p.key !== key) });
  };

  const save = async () => {
    if (!form.name_ar.trim()) { toast.error(t("dept_name_ar_required")); return; }
    try {
      if (editing) await api.put(`/departments/${editing.id}`, form);
      else await api.post("/departments", form);
      toast.success(t("success")); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (it) => {
    if (!window.confirm(`${t("dept_delete_confirm")} "${it.name_ar}"?`)) return;
    try { await api.delete(`/departments/${it.id}`); toast.success(t("success")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="departments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><Layers className="w-7 h-7 text-primary" /> {t("manage_departments")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("departments")}: {items.length}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="new-dept-btn"><Plus className="w-4 h-4 me-2" />{t("new_department")}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_department")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Label>{t("nameAr")}</Label>
                  <Input value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} placeholder={t("dept_example_ar")} data-testid="dept-name-ar" />
                </div>
                <div><Label>{t("department_code")}</Label>
                  <Input value={form.code || ""} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ACAD" data-testid="dept-code" />
                </div>
              </div>
              <div><Label>{t("nameEn")} <span className="text-xs text-muted-foreground">{t("optional_label")}</span></Label>
                <Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Academic Affairs" />
              </div>

              {/* Positions sub-list */}
              <div className="border border-primary/30 bg-primary/5 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <Briefcase className="w-4 h-4" /> {t("positions")} ({form.positions.length})
                </div>
                <p className="text-xs text-muted-foreground">{t("department_positions")}</p>

                <div className="grid sm:grid-cols-3 gap-2 items-end">
                  <div><Label className="text-xs">{t("position_label_ar")} <span className="text-destructive">*</span></Label>
                    <Input value={newPos.label_ar} onChange={e => setNewPos({ ...newPos, label_ar: e.target.value })} placeholder={t("pos_placeholder_ar")} data-testid="new-pos-ar" />
                  </div>
                  <div><Label className="text-xs">{t("position_label_en")}</Label>
                    <Input value={newPos.label_en} onChange={e => setNewPos({ ...newPos, label_en: e.target.value })} placeholder="Teacher" data-testid="new-pos-en" />
                  </div>
                  <Button size="sm" type="button" onClick={addPosition} data-testid="add-pos-btn">
                    <Plus className="w-4 h-4 me-1" /> {t("add_position")}
                  </Button>
                </div>

                {form.positions.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-4 border border-dashed border-border rounded">{t("no_positions")}</div>
                ) : (
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-primary/20">
                    {form.positions.map(p => (
                      <span key={p.key} className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full bg-primary/10 border border-primary/30">
                        {p.label_ar}
                        {p.label_en && <span className="text-[10px] text-muted-foreground">({p.label_en})</span>}
                        <button type="button" onClick={() => removePosition(p.key)} className="text-destructive hover:text-destructive/80" data-testid={`remove-pos-${p.key}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <Button onClick={save} className="w-full" data-testid="dept-save">{t("save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(d => (
          <Card key={d.id} className="p-5 border border-border shadow-none card-hover smooth" data-testid={`dept-${d.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center font-bold text-sm">
                {d.code || d.name_ar?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{d.name_ar}</div>
                {d.name && <div className="text-xs text-muted-foreground truncate">{d.name}</div>}
                <div className="text-xs text-muted-foreground mt-1">{d.positions?.length || 0} {t("positions")}</div>
              </div>
              <RowActions item={d} onEdit={openEdit} onDelete={remove} testIdPrefix="dept" />
            </div>
            {d.positions?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {d.positions.slice(0, 6).map(p => (
                  <span key={p.key} className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                    {p.label_ar}
                  </span>
                ))}
                {d.positions.length > 6 && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                    +{d.positions.length - 6}
                  </span>
                )}
              </div>
            )}
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="p-12 col-span-full text-center text-sm text-muted-foreground border-dashed">
            <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
            {t("no_data")}
          </Card>
        )}
      </div>
    </div>
  );
}
